<template>
    <div
        v-bind:class="{ 'pt-tour-step--sticky': isSticky }"
        class="pt-tour-step"
        :id="elId"
        :ref="targetRef"
        :data-popper-placement="params.placement"
    >
        <slot name="header">
            <div v-if="step.header" class="pt-tour--step__header">
                <div v-if="step.header.title" v-html="step.header.title"></div>
            </div>
        </slot>

        <slot name="content">
            <div class="pt-tour--step__content">
                <div v-if="step.content" v-html="step.content"></div>
                <div v-else>
                    This is a demo step! The id of this step is {{ hash }} and it targets {{ step.target }}.
                </div>
            </div>
        </slot>

        <slot name="actions">
            <div class="pt-tour--step__buttons">
                <button
                    @click.prevent="skip"
                    v-if="!isLast && isButtonEnabled('buttonSkip')"
                    class="pt-tour--step__button pt-tour--step__button-skip"
                >
                    {{ labels.buttonSkip }}
                </button>
                <button
                    @click.prevent="previousStep"
                    v-if="!isFirst && isButtonEnabled('buttonPrevious')"
                    class="pt-tour--step__button pt-tour--step__button-previous"
                >
                    {{ labels.buttonPrevious }}
                </button>
                <button
                    @click.prevent="nextStep"
                    v-if="!isLast && isButtonEnabled('buttonNext')"
                    class="pt-tour--step__button pt-tour--step__button-next"
                >
                    {{ labels.buttonNext }}
                </button>
                <button
                    @click.prevent="finish"
                    v-if="isLast && isButtonEnabled('buttonStop')"
                    class="pt-tour--step__button pt-tour--step__button-stop"
                >
                    {{ labels.buttonStop }}
                </button>
            </div>
        </slot>

        <div
            class="pt-tour--step__arrow"
            :class="{ 'pt-tour--step__arrow--dark': step.header && step.header.title }"
            data-popper-arrow
        ></div>
    </div>
</template>

<script lang="ts">
export default {
    name: 'pt-tour-step',
};
</script>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, getCurrentInstance, expose } from 'vue';
import { createPopper } from '@popperjs/core';
import jump from './jumper';
import sum from './hash-sum';
import { DEFAULT_STEP_OPTIONS, HIGHLIGHT } from './constants';

interface Props {
    step: Object;
    previousStep: () => void;
    nextStep: () => void;
    stop: () => void;
    skip: () => void;
    finish: () => void;
    isFirst: boolean;
    isLast: boolean;
    labels: Object;
    enabledButtons: Object;
    highlight: boolean;
    stopOnFail: boolean;
    debug: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    //skip: stop
    //finish: stop
});

const emit = defineEmits(['targetNotFound']);

const hash = sum(props.step.target);
const targetElement = document.querySelector(props.step.target);
const elId = 'pt-tour-step-' + hash;
const targetRef = ref(elId);

const params = computed(() => {
    return {
        ...DEFAULT_STEP_OPTIONS,
        ...{ highlight: props.highlight }, // Use global tour highlight setting first
        ...{ enabledButtons: Object.assign({}, props.enabledButtons) },
        ...props.step.params, // Then use local step parameters if defined
    };
});

const isSticky = computed(() => {
    return !props.step.target;
});

onMounted(() => {
    createStep();
});

onUnmounted(() => {
    removeHighlight();
});

function createStep() {
    if (props.debug) {
        console.log(
            '[Vue Tour] The target element ' + props.step.target + ' of .pt-tour-step[id="' + hash + '"] is:',
            targetElement,
        );
    }

    if (isSticky.value) {
        document.body.appendChild(targetRef.value);
    } else {
        if (targetElement) {
            enableScrolling();
            createHighlight();

            createPopper(targetElement, targetRef.value, params.value);
        } else {
            if (props.debug) {
                console.error(
                    '[Vue Tour] The target element ' +
                        props.step.target +
                        ' of .pt-tour-step[id="' +
                        hash +
                        '"] does not exist!',
                );
            }
            emit('targetNotFound', props.step);
            if (props.stopOnFail) {
                prop.stop();
            }
        }
    }
}

function enableScrolling() {
    if (params.value.enableScrolling) {
        if (props.step.duration || props.step.offset) {
            let jumpOptions = {
                duration: props.step.duration || 1000,
                offset: props.step.offset || 0,
                callback: undefined,
                a11y: false,
            };

            jump(targetElement, jumpOptions);
        } else {
            // Use the native scroll by default if no scroll options has been defined
            targetElement.scrollIntoView({ behavior: 'smooth' });
        }
    }
}

function isHighlightEnabled() {
    if (props.debug) {
        console.log(
            `[Vue Tour] Highlight is ${
                params.value.highlight ? 'enabled' : 'disabled'
            } for .pt-tour-step[id="${hash}"]`,
        );
    }
    return params.value.highlight;
}

function createHighlight() {
    if (isHighlightEnabled()) {
        document.body.classList.add(HIGHLIGHT.classes.active);
        const transitionValue = window.getComputedStyle(targetElement).getPropertyValue('transition');

        // Make sure our background doesn't flick on transitions
        if (transitionValue !== 'all 0s ease 0s') {
            targetElement.style.transition = `${transitionValue}, ${HIGHLIGHT.transition}`;
        }

        targetElement.classList.add(HIGHLIGHT.classes.targetHighlighted);
        // The element must have a position, if it doesn't have one, add a relative position class
        if (!targetElement.style.position) {
            targetElement.classList.add(HIGHLIGHT.classes.targetRelative);
        }
    } else {
        document.body.classList.remove(HIGHLIGHT.classes.active);
    }
}

function removeHighlight() {
    if (isHighlightEnabled()) {
        const target = targetElement;
        const currentTransition = targetElement.style.transition;
        targetElement.classList.remove(HIGHLIGHT.classes.targetHighlighted);
        targetElement.classList.remove(HIGHLIGHT.classes.targetRelative);
        // Remove our transition when step is finished.
        if (currentTransition.includes(HIGHLIGHT.transition)) {
            setTimeout(() => {
                target.style.transition = currentTransition.replace(`, ${HIGHLIGHT.transition}`, '');
            }, 0);
        }
    }
}

function isButtonEnabled(name) {
    return params.value.enabledButtons.hasOwnProperty(name) ? params.value.enabledButtons[name] : true;
}
</script>

<style lang="scss" scoped>
.pt-tour-step {
    max-width: 390px;
    color: #fff;
    text-align: center;
    position: relative;
    z-index: 10000;
    padding: 22px;
    border-radius: 8px;
    background: linear-gradient(to right, #0aa7ee, #0dd2ff);
    pointer-events: auto;

    &:before {
        content: '';
        width: 16px;
        height: 16px;
        position: absolute;
        left: -6px;
        top: 13px;
        background-color: #0aa7ee;
        border-radius: 3px;
        transform: rotate(45deg);
    }

    &--sticky {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        & .pt-tour--step__arrow {
            display: none;
        }
    }
}

.pt-tour--step__arrow,
.pt-tour--step__arrow::before {
    position: absolute;
    width: 10px;
    height: 10px;
    background: inherit;
}

.pt-tour--step__arrow {
    visibility: hidden;

    &--dark {
        &:before {
            background: #454d5d;
        }
    }
}

.pt-tour--step__arrow::before {
    visibility: visible;
    content: '';
    transform: rotate(45deg);
    margin-left: -5px;
}

.pt-tour-step[data-popper-placement^='top'] > .pt-tour--step__arrow {
    bottom: -5px;
}

.pt-tour-step[data-popper-placement^='bottom'] > .pt-tour--step__arrow {
    top: -5px;
}

.pt-tour-step[data-popper-placement^='right'] > .pt-tour--step__arrow {
    left: -5px;
}

.pt-tour-step[data-popper-placement^='left'] > .pt-tour--step__arrow {
    right: -5px;
}

/* Custom */

.pt-tour--step__header {
    margin: -1rem -1rem 0.5rem;
    padding: 0.5rem;
    background-color: #454d5d;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
}

.pt-tour--step__content {
    margin: 0 0 1rem 0;
}

.pt-tour--step__button {
    background: transparent;
    border: 0.05rem solid white;
    border-radius: 0.1rem;
    color: white;
    cursor: pointer;
    display: inline-block;
    font-size: 0.8rem;
    height: 1.8rem;
    line-height: 1rem;
    outline: none;
    margin: 0 0.2rem;
    padding: 0.35rem 0.4rem;
    text-align: center;
    text-decoration: none;
    transition: all 0.2s ease;
    vertical-align: middle;
    white-space: nowrap;

    &:hover {
        background-color: rgba(white, 0.95);
        color: #50596c;
    }
}
</style>
